/**
 * Inline icon set mirroring the SF Symbols the iOS app uses, so nav and empty
 * states read the same across platforms without pulling in an icon dependency.
 */

export type IconName =
  | "house"
  | "building"
  | "network"
  | "creditcard"
  | "wallet"
  | "sparkles"
  | "bag"
  | "archive"
  | "signout"
  | "plus"
  | "refresh"
  | "upload"
  | "check"
  | "alert"
  | "tray"
  | "folder"
  | "image"
  | "chart"
  | "grid"
  | "list";

const PATHS: Record<IconName, string> = {
  // house.fill
  house: "M3 10.5 12 3l9 7.5M5.5 9.5V20h13V9.5M9.5 20v-6h5v6",
  // building.columns — institutions
  building: "M3 9.5 12 4l9 5.5M4.5 9.5V20h15V9.5M8 12.5v4.5M12 12.5v4.5M16 12.5v4.5M3 20h18",
  // point.3.connected.trianglepath.dotted — networks
  network:
    "M12 4.5v5M6 19.5l5.2-7M18 19.5l-5.2-7M12 3a1.6 1.6 0 1 0 0 3.2A1.6 1.6 0 0 0 12 3ZM5 18.4a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2ZM19 18.4a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2Z",
  // creditcard.fill
  creditcard: "M3 7.5h18v10a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5v-10ZM3 10.5h18M6.5 15h4",
  // wallet.pass.fill — account types
  wallet:
    "M4 8.5A2 2 0 0 1 6 6.5h12a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-9ZM4 11h16M15 15h2",
  // sparkles.rectangle.stack.fill — card products
  sparkles:
    "M4 8.5A1.5 1.5 0 0 1 5.5 7h13A1.5 1.5 0 0 1 20 8.5v9A1.5 1.5 0 0 1 18.5 19h-13A1.5 1.5 0 0 1 4 17.5v-9ZM9 13l1-2 1 2 2 1-2 1-1 2-1-2-2-1 2-1ZM16 10.5l.6 1.2 1.2.6-1.2.6-.6 1.2-.6-1.2-1.2-.6 1.2-.6.6-1.2Z",
  // bag.fill — merchant icons
  bag: "M6 8.5V7a6 6 0 0 1 12 0v1.5M4.5 8.5h15l-1 11.5H5.5l-1-11.5ZM9.5 11.5v2M14.5 11.5v2",
  // archivebox.fill — legacy
  archive: "M3.5 7h17v3h-17V7ZM5 10v9.5h14V10M9.5 13.5h5",
  // rectangle.portrait.and.arrow.right
  signout:
    "M14 4.5H6.5A1.5 1.5 0 0 0 5 6v12a1.5 1.5 0 0 0 1.5 1.5H14M16 8.5l3.5 3.5L16 15.5M11 12h8.5",
  plus: "M12 5.5v13M5.5 12h13",
  // arrow.triangle.2.circlepath
  refresh:
    "M4.5 12a7.5 7.5 0 0 1 12.8-5.3M19.5 12a7.5 7.5 0 0 1-12.8 5.3M17 3.5v3.5h-3.5M7 20.5V17h3.5",
  // doc.badge.arrow.up
  upload: "M12 19V8.5M8 12l4-3.5 4 3.5M5 20h14",
  check: "M5 12.5 9.5 17 19 7.5",
  // exclamationmark.triangle.fill
  alert: "M12 4.5 21 19.5H3L12 4.5ZM12 10v4.5M12 16.8v.2",
  // tray — empty states
  tray: "M4 13.5 6.5 6h11L20 13.5M4 13.5V18a1.5 1.5 0 0 0 1.5 1.5h13A1.5 1.5 0 0 0 20 18v-4.5M4 13.5h4l1 2h6l1-2h4",
  // folder.fill — grouped assets
  folder: "M3.5 7.5A1.5 1.5 0 0 1 5 6h5l2 2h7A1.5 1.5 0 0 1 20.5 9.5v8A1.5 1.5 0 0 1 19 19H5a1.5 1.5 0 0 1-1.5-1.5v-10Z",
  // photo.fill — image assets
  image: "M4 5.5h16v13H4v-13ZM7.5 9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM5.5 17l4.5-5 3 3 2-2 3.5 4",
  // chart.line.uptrend.xyaxis — investment assets
  chart: "M4 4v16h16M7 15.5l3.5-4 3 2.5L19 8M19 8h-3.5M19 8v3.5",
  // square.grid.2x2 — gallery
  grid: "M4.5 4.5h6v6h-6v-6ZM13.5 4.5h6v6h-6v-6ZM4.5 13.5h6v6h-6v-6ZM13.5 13.5h6v6h-6v-6Z",
  // list.bullet — list
  list: "M8 6.5h12M8 12h12M8 17.5h12M4.5 6.5h.01M4.5 12h.01M4.5 17.5h.01",
};

export function Icon({
  name,
  size = 18,
  strokeWidth = 1.7,
}: {
  name: IconName;
  size?: number;
  strokeWidth?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
